import React, { FC, useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { VpdThreshold } from 'models/restTypes';
import { CropType } from 'models/shared';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const VpdThresholdsTable: FC<{
  thresholds: VpdThreshold[];
  isLoading: boolean;
  cropTypes: CropType[];
  selectedThreshold: VpdThreshold | null;
  onSelectionChange: (threshold: VpdThreshold | null) => void;
}> = observer(
  ({
    thresholds,
    isLoading,
    selectedThreshold,
    cropTypes,
    onSelectionChange,
  }) => {
    const { t } = useTranslation('thresholds');

    const { t: growerT } = useTranslation('grower');

    const cropTypeMap = useMemo(() => {
      const tempMap = new Map<number, CropType>();
      cropTypes.forEach((crop) => tempMap.set(crop.id, crop));
      return tempMap;
    }, [cropTypes]);

    // Enrich thresholds with translated crop name
    const enrichedThresholds = useMemo(() => {
      return thresholds.map((threshold) => ({
        ...threshold,
        cropName: cropTypeMap.get(threshold.cropId)?.name || '',
      }));
    }, [thresholds, cropTypeMap]);

    // Find the enriched version of the selected threshold
    const selectedEnrichedThreshold = selectedThreshold
      ? enrichedThresholds.find(
        (th) => th.cropId === selectedThreshold.cropId,
      ) || null
      : null;

    const [filters] = useState({
      global: { value: '', matchMode: FilterMatchMode.CONTAINS },
      cropId: { value: '', matchMode: FilterMatchMode.CONTAINS },
      cropName: { value: '', matchMode: FilterMatchMode.CONTAINS },
    });

    const onRowClick = useCallback(
      ({ data }: DataTableRowClickEvent) => {
        // Find the original threshold to pass to parent
        const originalThreshold = thresholds.find(
          (th) => th.cropId === data.cropId,
        );

        onSelectionChange(originalThreshold || (data as VpdThreshold));
      },
      [onSelectionChange, thresholds],
    );

    const numberOrDashBody = useCallback((value: number | null) => {
      return value !== null ? value : '-';
    }, []);

    const mdsMinBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.MdsMin);
      },
      [numberOrDashBody],
    );

    const vpdMaxBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.VPDmax);
      },
      [numberOrDashBody],
    );

    const mdsRateBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.MDSrate);
      },
      [numberOrDashBody],
    );

    const pmbDeltaBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.PMBDelta);
      },
      [numberOrDashBody],
    );

    const vpdNightMaxBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.VPDNIGHTmax);
      },
      [numberOrDashBody],
    );

    const pmbReductionBody = useCallback(
      (rowData: VpdThreshold) => {
        return numberOrDashBody(rowData.detectionParams.PMBreduction);
      },
      [numberOrDashBody],
    );

    if (isLoading) {
      return <Loading />;
    }

    return (
      <StyledGrid flex={1}>
        <DataTableContainer elevation={3}>
          <DataTable
            id="VpdThresholdsTable"
            showGridlines
            resizableColumns
            value={enrichedThresholds}
            selectionMode="single"
            selection={selectedEnrichedThreshold}
            onSelectionChange={(e) => {
              // Find the original threshold to pass to parent
              const originalThreshold = thresholds.find(
                (th) => th.cropId === e.value?.cropId,
              );

              onSelectionChange(originalThreshold || (e.value as VpdThreshold));
            }}
            scrollable
            scrollHeight="flex"
            dataKey="cropId"
            emptyMessage={t('empty_message')}
            stripedRows
            filters={filters}
            onRowClick={onRowClick}
            filterDisplay="row"
            globalFilterFields={['cropId', 'cropName']}
            paginator
            rows={10}
          >
            <Column selectionMode="single" headerStyle={{ width: '3rem' }} />
            <Column field="cropName" header={growerT('crop')} sortable filter />
            <Column field="cropId" header={t('crop_id')} sortable filter />
            <Column
              field="detectionParams.MdsMin"
              header={t('mds_min')}
              sortable
              body={mdsMinBody}
            />
            <Column
              field="detectionParams.VPDmax"
              header={t('vpd_max')}
              sortable
              body={vpdMaxBody}
            />
            <Column
              field="detectionParams.MDSrate"
              header={t('mds_rate')}
              sortable
              body={mdsRateBody}
            />
            <Column
              field="detectionParams.PMBDelta"
              header={t('pmb_delta')}
              sortable
              body={pmbDeltaBody}
            />
            <Column
              field="detectionParams.VPDNIGHTmax"
              header={t('vpd_night_max')}
              sortable
              body={vpdNightMaxBody}
            />
            <Column
              field="detectionParams.PMBreduction"
              header={t('pmb_reduction')}
              sortable
              body={pmbReductionBody}
            />
          </DataTable>
        </DataTableContainer>
      </StyledGrid>
    );
  },
);

export default VpdThresholdsTable;
